import React, { useState } from 'react';
import styled from '@emotion/styled';

import InputWrapper from './InputWrapper';
import Button from './Button';
import Pagination from './Pagination';
import SpinnerBlock from './SpinnerBlock';
import Checkbox from './Checkbox';
import { showModal } from '../../actions/modal';
import { useDispatch } from 'react-redux';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  position: relative;

  .block-subtitle {
    color: #98a6ad;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 700;
  }
  .table-item {
    .item-remove {
      display: none;
    }

    &:hover {
      .item-remove {
        display: block;
        cursor: pointer;
      }
    }
  }

  .edit-item {
    .fe-edit-2 {
      visibility: hidden;
      margin-left: 5px;
    }

    &:hover {
      .fe-edit-2 {
        visibility: initial;
        cursor: pointer;
      }
    }
  }

  .actions-td {
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
    height: 70px;
    min-width: 100px;
  }

  .barcode-icon {
    transform: rotate(90deg);
  }

  .item-modal {
    margin-left: 16px;
  }
`;

const Row = (props) => {
  const {
    data,
    item,
    selectKey,
    selectValue,
    selectable,
    removable,
    copyable,
    isEditable = true,
    isRemovable = false,
    isCopyable = false,
    isSelectable = true,
    handleEditItem,
    handleCopyItem,
    handleRemoveItem,
    handleSelect,
    className,
    rowIndex,
    selected,
    handleUnloadingSetOfGoodsChestnyznak,
    handleOpenModal,
    id,
    t,
    ...rest
  } = props;

  return (
    <tr className="table-item" {...rest}>
      {selectable ? (
        isSelectable ? (
          <td
            className="checkbox-td"
            onClick={() => handleSelect(selectKey || item.id, selectValue)}
          >
            <Checkbox id={`checkbox-${item.id}`} checked={selected[selectKey || item.id]} />
          </td>
        ) : (
          <td />
        )
      ) : null}
      {data.map((d, i) => (
        <td
          key={i}
          className={`${d.editItem ? 'edit-item' : ''} ${d.className || ''}`}
          style={d.style || {}}
          onClick={d.onClick || (() => false)}
        >
          {d.value}
          {d.editItem && isEditable ? (
            <i className="fe-edit-2" onClick={() => handleEditItem(item)} />
          ) : null}
        </td>
      ))}
      {removable || copyable ? (
        <td>
          <div className="actions-td">
            {copyable && isCopyable ? (
              <div
                className="item-remove mr-2"
                onClick={() => handleCopyItem(item)}
                title={t('Скопировать задание на сборку')}
              >
                <i className="fe-copy" />
              </div>
            ) : null}
            {removable && isRemovable ? (
              <div
                className="item-remove mr-2"
                title={t('Удалить')}
                onClick={() => handleRemoveItem(item, rowIndex)}
              >
                <i className="fe-trash-2" />
              </div>
            ) : (
              <div />
            )}
            {id === 'physicalSetsJournal' ? (
              <>
                <div
                  className="item-remove barcode-icon"
                  title={t('Выгрузить ЧЗ')}
                  onClick={() => handleUnloadingSetOfGoodsChestnyznak(item.id)}
                >
                  <i className="fe-align-justify" />
                </div>
                <div
                  className="item-remove barcode-icon item-modal"
                  title={t('Данные о ЧЗ комплекта')}
                  onClick={() => handleOpenModal(item)}
                >
                  <i className="fe-book" />
                </div>
              </>
            ) : null}
          </div>
        </td>
      ) : null}
    </tr>
  );
};

const Table = (props) => {
  const {
    id,
    className = {},
    style = {},
    smallTitle,
    data,
    searchable,
    searchPlaceholder = 'Поиск',
    creatable,
    selectable,
    createLabel = '',
    copyable,
    removable,
    pagination,
    totalItems = 0,
    perPage,
    loading = false,
    onlyOneSelected,
    bottomCreateButton,
    handleSearch,
    handleSelect,
    handleNewItem,
    handleCopyItem = () => false,
    handleRemoveItem = () => false,
    handleEditItem = () => false,
    handleChangePage = () => false,
    handleUnloadingSetOfGoodsChestnyznak,
  } = props;

  const { t } = useAutoTranslation();

  const dispatch = useDispatch();

  const handleOpenModal = (checkedItem) => {
    dispatch(
      showModal('AdditionalDataSet', {
        checkedItem,
      }),
    );
  };

  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState({});

  const handlePageSelect = (pageIndex) => {
    setPage(pageIndex);
    handleChangePage(pageIndex);
  };

  const handleItemsSelect = (itemId, itemValue) => {
    const newSelected = !onlyOneSelected
      ? {
          ...selected,
          [itemId]: itemValue ? (selected[itemId] ? null : itemValue) : !selected[itemId],
        }
      : { [itemId]: itemValue ? (selected[itemId] ? null : itemValue) : !selected[itemId] };

    setSelected(newSelected);
    handleSelect(newSelected);
  };

  const handleSearchChange = (e) => {
    handleSearch(e);
    setPage(0);
    setSelected({});
  };

  return (
    <Wrapper className={className} style={style} data-testid="table-wrap">
      <SpinnerBlock show={loading} />
      {searchable || (creatable && !bottomCreateButton) || smallTitle ? (
        <div className="row mt-2" style={{ alignItems: 'center' }}>
          <div className="col-4">
            {searchable ? (
              <InputWrapper
                noLabel
                id={`${id}Search`}
                testId="table-search"
                placeholder={t(searchPlaceholder)}
                onChange={handleSearchChange}
              />
            ) : null}
            {smallTitle ? <div className="block-subtitle">{smallTitle}</div> : null}
          </div>
          <div className="col-lg-8">
            <div className="text-lg-right">
              {creatable && !bottomCreateButton ? (
                <Button
                  classType="btn-danger"
                  icon="fe-plus"
                  text={createLabel}
                  onClick={handleNewItem}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="fixed-table-container mt-2">
        <div className="table-responsive">
          <table className="table table-centered table-hover">
            <thead className="thead-light">
              <tr>
                {selectable ? <th /> : null}
                {data?.cols.map((c, i) => <th key={i}>{c}</th>) ?? []}
                {removable ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {data?.rows.map((r, i) => (
                <>
                  <Row
                    key={r.key}
                    {...r}
                    rowIndex={i}
                    selectable={selectable}
                    removable={removable}
                    copyable={copyable}
                    selected={selected}
                    handleEditItem={handleEditItem}
                    handleCopyItem={handleCopyItem}
                    handleRemoveItem={handleRemoveItem}
                    handleSelect={handleItemsSelect}
                    handleUnloadingSetOfGoodsChestnyznak={handleUnloadingSetOfGoodsChestnyznak}
                    handleOpenModal={handleOpenModal}
                    id={id}
                    t={t}
                  />
                  {r?.additionalRows?.map((a, j) => (
                    <Row
                      key={a.key}
                      {...a}
                      selected={selected}
                      selectable={selectable}
                      isSelectable={a.isSelectable}
                      rowIndex={j}
                      handleSelect={handleItemsSelect}
                      handleUnloadingSetOfGoodsChestnyznak={handleUnloadingSetOfGoodsChestnyznak}
                      handleOpenModal={handleOpenModal}
                      t={t}
                    />
                  )) ?? null}
                </>
              )) ?? []}
            </tbody>
          </table>
          {creatable && bottomCreateButton ? (
            <div
              style={{ color: '#7e57c2', cursor: 'pointer', marginBottom: '15px' }}
              onClick={handleNewItem}
            >
              {createLabel}
            </div>
          ) : null}

          {pagination ? (
            <Pagination
              total={totalItems}
              currentPage={Number(page)}
              perPage={perPage}
              onPageSelect={handlePageSelect}
            />
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
};

export default Table;
